/**
 * jurisdictionOrder — the user's preferred order of jurisdictions.
 *
 * The key is the ISO-3166 alpha-2 code on every available-sources row
 * (`jurisdiction`: "BG", "NO", "SE"…), used verbatim. Nothing is derived from
 * the display name, which is localised and would not round-trip.
 *
 * `available-sources` already returns rows in the stored order, so the picker
 * renders in RECEIVED order by default. These helpers exist for the reorder
 * interaction itself: moving a group, producing the full sequence to save,
 * and reconciling against what the server actually stored.
 *
 * Everything here is pure.
 */

/** Where a code sits in an order, or -1. Case-insensitive; keys are stored upper. */
function indexOfCode(order: string[], code: string): number {
  const needle = code.trim().toUpperCase();
  return order.findIndex((c) => String(c).trim().toUpperCase() === needle);
}

/** Normalise to comparable keys, dropping blanks and duplicates. */
export function normaliseOrder(order: string[] | null | undefined): string[] {
  const seen: string[] = [];
  (order || []).forEach((raw) => {
    const code = String(raw ?? '').trim().toUpperCase();
    if (code && !seen.includes(code)) seen.push(code);
  });
  return seen;
}

/**
 * Sort codes by a stored order.
 *
 * A jurisdiction the order does not mention keeps its RECEIVED position,
 * after everything that is ordered — a newly-added jurisdiction should appear,
 * not disappear because the stored preference predates it.
 */
export function applyOrder(codes: string[], order: string[]): string[] {
  const preference = normaliseOrder(order);
  const known = codes.filter((code) => indexOfCode(preference, code) >= 0);
  const rest = codes.filter((code) => indexOfCode(preference, code) < 0);

  known.sort((a, b) => indexOfCode(preference, a) - indexOfCode(preference, b));
  return [...known, ...rest];
}

/**
 * Move a jurisdiction to a position in the visible sequence.
 *
 * Operates on the codes AS DISPLAYED, so what the user sees is what gets
 * saved — a stored order that mentions jurisdictions this user cannot see
 * must not shift the result.
 */
export function moveJurisdiction(codes: string[], code: string, toIndex: number): string[] {
  const from = indexOfCode(codes, code);
  if (from < 0) return codes;

  const target = Math.min(Math.max(toIndex, 0), codes.length - 1);
  if (target === from) return codes;

  const next = codes.slice();
  const [moved] = next.splice(from, 1);
  next.splice(target, 0, moved);
  return next;
}

/**
 * The sequence to PUT: the whole thing, every time.
 *
 * The server stores exactly what it is sent, so anything omitted is dropped.
 * Codes the user cannot currently see are preserved at the end, because a
 * jurisdiction hidden by a filter or a licence change must not be silently
 * erased from a preference the user never touched.
 */
export function fullSequence(visible: string[], storedOrder: string[]): string[] {
  const shown = normaliseOrder(visible);
  const carried = normaliseOrder(storedOrder).filter((code) => !shown.includes(code));
  return [...shown, ...carried];
}

/**
 * What the server actually stored wins.
 *
 * It echoes the accepted order plus any keys it ignored (retired or unknown
 * jurisdictions), still with a 200. Adopting the response rather than the
 * optimistic guess keeps the client honest about what was persisted.
 */
export function reconcileOrder(
  optimistic: string[],
  stored: string[] | null | undefined
): string[] {
  const accepted = normaliseOrder(stored);
  return accepted.length > 0 ? accepted : normaliseOrder(optimistic);
}
